import type {
	ProjectMediaCopy,
	ProjectMediaUploadCopy,
} from "@zplanem/translations"
import { useEffect, useState } from "react"
import type { PickedFile } from "./project-media-picked-files"

/**
 * One picked file in the add modal: thumbnail on the left, title and
 * description stacked on the right, remove on the far right.
 *
 * Only images can be previewed client-side — there is no PDF rasteriser in
 * the web bundle and server thumbnails only exist after upload — so pdf and
 * video get the uppercase kind badge already used by ticket attachments and
 * the document list.
 */
export function ProjectMediaFileRow({
	row,
	copy,
	uploadCopy,
	titleMax,
	descriptionMax,
	busy,
	onChange,
	onRemove,
}: {
	row: PickedFile
	copy: ProjectMediaCopy
	uploadCopy: ProjectMediaUploadCopy
	titleMax: number
	descriptionMax: number
	/** The batch is uploading — nothing in the list may be touched. */
	busy: boolean
	onChange: (patch: { title?: string; description?: string }) => void
	onRemove: () => void
}) {
	const [previewUrl, setPreviewUrl] = useState<string | null>(null)

	// One object URL per image row, revoked when the row unmounts (removed,
	// or the modal closed) so a long picking session can't leak blobs.
	useEffect(() => {
		if (row.kind !== "image") {
			setPreviewUrl(null)
			return
		}
		const url = URL.createObjectURL(row.file)
		setPreviewUrl(url)
		return () => URL.revokeObjectURL(url)
	}, [row.kind, row.file])

	const locked = busy || row.status === "uploading" || row.status === "done"
	const fieldClass =
		"w-full rounded-lg bg-background border border-border px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-ring disabled:opacity-60"

	return (
		<li className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3">
			{previewUrl ? (
				<img
					src={previewUrl}
					alt=""
					className="h-16 w-16 shrink-0 rounded-md object-cover"
				/>
			) : (
				<span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-md bg-background text-[10px] font-semibold uppercase text-muted-foreground">
					{row.kind}
				</span>
			)}

			<div className="min-w-0 flex-1 space-y-2">
				<input
					type="text"
					value={row.title}
					disabled={locked}
					maxLength={titleMax}
					aria-label={`${copy.titleLabel} — ${row.file.name}`}
					placeholder={copy.titleLabel}
					onChange={(e) => onChange({ title: e.target.value })}
					className={fieldClass}
				/>
				<textarea
					value={row.description}
					disabled={locked}
					maxLength={descriptionMax}
					rows={2}
					aria-label={`${copy.descriptionLabel} — ${row.file.name}`}
					placeholder={copy.descriptionLabel}
					onChange={(e) => onChange({ description: e.target.value })}
					className={`${fieldClass} resize-none`}
				/>
				<p className="truncate text-xs text-muted-foreground">
					{row.file.name}
				</p>
			</div>

			<div className="flex w-20 shrink-0 flex-col items-end gap-1">
				{row.status === "done" ? null : (
					<button
						type="button"
						onClick={onRemove}
						disabled={busy || row.status === "uploading"}
						aria-label={fillName(uploadCopy.removeFile, row.file.name)}
						className="rounded-md px-2 py-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
					>
						×
					</button>
				)}
				<RowStatus row={row} uploadCopy={uploadCopy} />
			</div>
		</li>
	)
}

function RowStatus({
	row,
	uploadCopy,
}: {
	row: PickedFile
	uploadCopy: ProjectMediaUploadCopy
}) {
	if (row.status === "ready") return null
	const tone =
		row.status === "failed" ? "text-destructive" : "text-muted-foreground"
	const label =
		row.status === "uploading"
			? uploadCopy.statusUploading
			: row.status === "done"
				? uploadCopy.statusDone
				: uploadCopy.statusFailed
	// Deliberately not a live region: one per row would queue a burst of
	// anonymous "Uploading…/Added" announcements during a batch. The batch
	// outcome is announced once, by the modal's role="alert" summary.
	return <span className={`text-right text-xs ${tone}`}>{label}</span>
}

/**
 * Substitute `{name}`. `String.replace` expands `$&`, `` $` `` and `$'` in the
 * replacement, which would mangle a filename containing them, so this splits
 * and joins instead — the same way `plural()` handles `{count}`.
 */
function fillName(template: string, name: string): string {
	return template.split("{name}").join(name)
}
